/**
 * PsicoSalud — Hook useStickyShrink.
 *
 * Detecta cuándo el usuario hace scroll más allá de un sentinel
 * colocado al tope de la página, y devuelve un flag para activar
 * el modo compacto del encabezado. Pensado para vistas largas
 * (historial clínico completo) donde el page-header debe reducirse
 * y quedar sticky arriba sin perder los datos clave del paciente.
 *
 *   const { sentinelRef, isScrolled } = useStickyShrink<HTMLDivElement>();
 *
 *   <header className={isScrolled ? 'is-sticky-shrink is-scrolled' : 'is-sticky-shrink'}>
 *     …
 *   </header>
 *   <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
 *
 * Usa IntersectionObserver (no listener de scroll) para mantener
 * el costo fijo independiente del tamaño del DOM. La animación
 * del shrink vive en CSS (.is-sticky-shrink con transition); este
 * hook solo decide cuándo añadir o quitar la clase.
 */

import { useEffect, useRef, useState } from 'react';

export function useStickyShrink<T extends Element>() {
  const [isScrolled, setIsScrolled] = useState(false);
  const sentinelRef = useRef<T | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // El sentinel está en la parte de arriba de la página. Si sale
        // del viewport por arriba (boundingClientRect.top < 0), significa
        // que el usuario hizo scroll hacia abajo más allá del header.
        const aboveViewport = entry.boundingClientRect.top < 0;
        setIsScrolled(!entry.isIntersecting && aboveViewport);
      },
      { threshold: 0, rootMargin: '0px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return { sentinelRef, isScrolled } as const;
}
